import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type {
  ReportFormat,
  ReportRunStatus,
} from 'api/analytics-reports/types';
import type { AnalyticsReportCreatedBy } from '../types/AnalyticsReport';

export interface ActiveReportRun {
  reportId: number;
  runId: number;
  name: string;
  profiles: number[];
  createdBy: AnalyticsReportCreatedBy;
  createdAt: string;
  format: ReportFormat;
  generatingStatus: Extract<ReportRunStatus, 'pending' | 'running'>;
  autoDownloadOnComplete?: boolean;
}

interface ActiveReportRunsContextValue {
  activeRuns: ActiveReportRun[];
  addActiveRun: (run: ActiveReportRun) => void;
  removeActiveRun: (runId: number) => void;
  updateActiveRunStatus: (
    runId: number,
    status: ActiveReportRun['generatingStatus'],
  ) => void;
}

const ActiveReportRunsContext = createContext<
  ActiveReportRunsContextValue | undefined
>(undefined);

interface Props {
  children: ReactNode;
}

export const ActiveReportRunsProvider: FC<Props> = ({ children }) => {
  const [activeRuns, setActiveRuns] = useState<ActiveReportRun[]>([]);

  const addActiveRun = useCallback((run: ActiveReportRun) => {
    setActiveRuns((current) => {
      if (
        current.some(
          (item) => item.runId === run.runId && item.reportId === run.reportId,
        )
      ) {
        return current;
      }
      return [run, ...current];
    });
  }, []);

  const removeActiveRun = useCallback((runId: number) => {
    setActiveRuns((current) => current.filter((item) => item.runId !== runId));
  }, []);

  const updateActiveRunStatus = useCallback(
    (runId: number, status: ActiveReportRun['generatingStatus']) => {
      setActiveRuns((current) =>
        current.map((item) =>
          item.runId === runId ? { ...item, generatingStatus: status } : item,
        ),
      );
    },
    [],
  );

  const value = useMemo(
    (): ActiveReportRunsContextValue => ({
      activeRuns,
      addActiveRun,
      removeActiveRun,
      updateActiveRunStatus,
    }),
    [activeRuns, addActiveRun, removeActiveRun, updateActiveRunStatus],
  );

  return (
    <ActiveReportRunsContext.Provider value={value}>
      {children}
    </ActiveReportRunsContext.Provider>
  );
};

export function useActiveReportRuns(): ActiveReportRunsContextValue {
  const context = useContext(ActiveReportRunsContext);
  if (!context) {
    throw new Error(
      'useActiveReportRuns must be used within ActiveReportRunsProvider',
    );
  }
  return context;
}
